import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import type { useGetSummaryCalculations_Booking$key } from "./__generated__/useGetSummaryCalculations_Booking.graphql";
import {
  getAggregatedOrderFinancialSummary,
  readOrderFinancialSummary,
} from "./getAggregatedOrderFinancialSummary";

export const useGetSummaryCalculations = ({
  fragmentRef,
}: {
  fragmentRef: useGetSummaryCalculations_Booking$key | null;
}) => {
  const data = useFragment(
    useGetSummaryCalculationsFragmentString,
    fragmentRef
  );

  const requiresPrePayment =
    data?.paymentRequirements?.prePaymentRequirement === "FULL";

  const orderFinancialSummaries =
    data?.orders?.edges?.map((edge) => readOrderFinancialSummary(edge?.node)) ??
    [];

  const {
    subtotalBeforeDiscount: subtotal,
    totalAfterDiscount: total,
    totalDiscounts,
    totalTaxes,
    serviceChargeTotal,
  } = getAggregatedOrderFinancialSummary(orderFinancialSummaries);
  const { totalDue, totalPaid } = (data?.orders?.edges ?? []).reduce(
    ({ totalDue, totalPaid }, cur) => {
      const bookingOrder = cur?.node;
      const orderTotal = bookingOrder?.total ?? 0;
      const isFullyPaid = bookingOrder?.status === "FULLY_PAID";

      return {
        totalDue: isFullyPaid ? totalDue : totalDue + orderTotal,
        totalPaid: isFullyPaid ? totalPaid + orderTotal : totalPaid,
      };
    },
    { totalDue: 0, totalPaid: 0 }
  );
  const serverDiscountTotal = orderFinancialSummaries.reduce(
    (total, order) => total + (order?.totalDiscount ?? 0),
    0
  );

  const totalDueAfterCredits = data?.totalDueAfterCredits;

  const totalDueNow = requiresPrePayment ? totalDueAfterCredits ?? totalDue : 0;
  const totalDueArrival = requiresPrePayment ? 0 : totalDue;

  return {
    subtotal,
    total,
    totalDiscounts,
    totalPaid,
    totalTaxes,
    totalDueNow,
    totalDueArrival,
    serviceChargeTotal,
    totalDueAfterCredits,
    serverDiscountTotal,
  } as const;
};

export const useGetSummaryCalculationsFragmentString = graphql`
  fragment useGetSummaryCalculations_Booking on Booking {
    paymentRequirements {
      prePaymentRequirement
    }
    id
    __typename
    totalDueAfterCredits

    orders {
      edges {
        node {
          status
          total
          orderPayments {
            edges {
              node {
                amount
                squareGiftCard {
                  id
                }
              }
            }
          }
          ...getAggregatedOrderFinancialSummary_order
        }
      }
    }
  }
`;
